/*
 * Remote-Changes-Handler — SyncService.onRemoteChanges-Callback.
 *
 * Wird in main.js via createRemoteChangesHandler(ctx, saveContent) erzeugt
 * und am SyncService registriert. Spielt Server-Aenderungen (anderes Geraet
 * oder Agent-Auto-Apply) in den aktiven Editor ein.
 *
 * Aus main.js extrahiert (System-Review 2026-06-10, B3), damit der
 * Datenverlust-Fix testbar ist: Tipparbeit aus dem 1s-Flush-Debounce-Fenster
 * MUSS nach IndexedDB geschrieben werden, BEVOR loadProject das In-Memory-
 * Modell durch den IDB-Stand ersetzt.
 */

import { Position } from '../editor/EditorModel.js'
import { refreshSpellCheckAfterRemoteSync } from '../spellcheck/syncRefresh.js'

/**
 * Erzeugt den onRemoteChanges-Callback fuer den SyncService.
 *
 * @param {Object} ctx - App-Kontext (projectManager, editor, currentProjectId, ...)
 * @param {Function} saveContent - ueberfuehrt EditorModel-Dirty synchron in
 *   ProjectManager-Dirty (aus persistence.js; laeuft sonst nur im Render-Loop)
 * @returns {(projectId: string, changes: Object[], deletedCount?: number) => Promise<void>}
 */
export function createRemoteChangesHandler(ctx, saveContent) {
    return async (projectId, changes, deletedCount = 0) => {
        // In-Memory Version-Cache verwerfen — Server hat gerade direkt
        // in die DB geschrieben, der Cache waere jetzt stale.
        ctx.projectManager.invalidateVersionMap(projectId)

        if (projectId !== ctx.currentProjectId) return
        if (!ctx.editor) return

        // B3 (System-Review 2026-06-10): Tipparbeit aus dem 1s-Flush-
        // Debounce-Fenster ZUERST nach IndexedDB schreiben, bevor
        // loadProject unten das In-Memory-Modell durch den IDB-Stand
        // ersetzt — sonst sind die letzten Eingaben weder im Modell noch
        // in IDB (Pattern: flushBeforeAck). flushNow() rehydriert die
        // Paragraph-Meta nach dem invalidateVersionMap oben frisch aus dem
        // Store und enqueued die Absaetze fuer den naechsten Push (LWW).
        try { saveContent() }
        catch (err) { console.warn('[onRemoteChanges] saveContent threw:', err) }
        try { await ctx.projectManager.flushNow() }
        catch (err) { console.warn('[onRemoteChanges] flushNow threw:', err) }

        // Projekt aus IndexedDB neu laden (enthält bereits die Remote-Änderungen)
        const { project, paragraphs } = await ctx.projectManager.loadProject(projectId)

        if (paragraphs && paragraphs.length > 0) {
            // Cursor-Position merken (paragraphId + offset + field)
            const oldCursor = ctx.editor.model.cursor
                ? ctx.editor.model.cursor.clone()
                : null

            // Server-Aenderungen als Remote-Apply-Block einspielen —
            // saveContent() im Render-Loop skippt diesen Batch dank
            // des Remote-Flags.
            ctx.editor.applyRemoteChanges(() => {
                ctx.editor.model.setParagraphsRT(paragraphs)
                ctx.editor.model._firstSectionLocked = !!project.firstSectionLocked
            })

            // Cursor wiederherstellen: moeglichst nah an alter Position
            if (oldCursor) {
                const para = ctx.editor.model.getParagraph(oldCursor.paragraphId)
                if (para) {
                    // Absatz existiert noch — Offset clampen falls Text kuerzer
                    const fieldText = oldCursor.field === 'note' ? para.note
                        : oldCursor.field === 'info' ? para.info : para.text
                    const safeOffset = Math.min(oldCursor.offset, (fieldText || '').length)
                    ctx.editor.model.cursor = new Position(
                        oldCursor.paragraphId, safeOffset, oldCursor.field
                    )
                }
                // Absatz geloescht: setParagraphsRT hat Cursor bereits auf
                // den ersten Absatz gesetzt — das ist die sinnvollste Fallback-Position
            }

            ctx.editor.view.markDirty()
        }

        // Spellcheck nach Remote-Sync nachziehen — setParagraphsRT triggert
        // KEINE _markParagraphDirty-Events; siehe `syncRefresh.js` fuer
        // die genaue Begruendung. Ohne diesen Refresh bleibt die Welle
        // fuer remote-eingespielte Aenderungen stale, bis der User tippt.
        refreshSpellCheckAfterRemoteSync(
            ctx.spellCheckManager,
            ctx.editor.model.paragraphs,
            changes,
            deletedCount
        )

        // Alle Views dirty markieren (fuer visuelles Re-Render)
        ctx.layout.isDirty = true
        ctx.layout.borderDirty = true
        ctx.renderer.markBorderDirty()
        ctx.screen.markDirty()

        if (ctx.coreConsole) {
            const parts = []
            if (changes.length > 0) parts.push(`${changes.length} updated`)
            if (deletedCount > 0) parts.push(`${deletedCount} deleted`)
            ctx.coreConsole.log(`Sync: ${parts.join(', ')} from server`)
        }
    }
}
